import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Laboratory } from '@shared/constants/kit.constants';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  map,
  of,
  take,
} from 'rxjs';
import { UserConstraints, UserData } from '../core.types';
import { Logger } from './logger.service';
import { UserAccountService } from './user-accounts.service';

@Injectable({
  providedIn: 'root',
})
export class AccountUtilsService {
  private account = inject(UserAccountService);

  private readonly log = new Logger(this.constructor.name);

  public availableLaboratories$: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>(Object.values(Laboratory));

  // null until the first load completes, so consumers can distinguish
  // "not loaded yet" from "loaded with no permissions"
  private readonly userState = signal<UserData | null>(null);

  public readonly permissions: Signal<string[]> = computed(
    () => this.userState()?.permissions ?? []
  );
  public readonly permissionsLoaded: Signal<boolean> = computed(
    () => this.userState() !== null
  );
  public readonly constraints: Signal<UserConstraints | null> = computed(
    () => this.userState()?.constraints ?? null
  );
  public readonly email: Signal<string | null> = computed(
    () => this.userState()?.email ?? null
  );

  private readonly userState$ = toObservable(this.userState);

  public loadPermissions(): void {
    this.account
      .getUserMetadata()
      .pipe(
        take(1),
        catchError((error) => {
          this.log.error('Failed to load user permissions', error);
          return of<UserData>({ permissions: [], constraints: null });
        })
      )
      .subscribe((response: UserData) =>
        this.userState.set(response ?? { permissions: [], constraints: null })
      );
  }

  public clearPermissions(): void {
    this.userState.set(null);
  }

  public hasRequiredPermission(required?: string | string[]): boolean {
    if (!required || required.length === 0) {
      return true;
    }

    const requiredPermissions = Array.isArray(required) ? required : [required];
    const permissions = this.permissions();

    return requiredPermissions.every((permission) =>
      permissions.includes(permission)
    );
  }

  // Waits for permissions to load before emitting; for route guards and
  // rxjs pipelines that must not evaluate against an unloaded state.
  public hasRequiredPermission$(
    required?: string | string[]
  ): Observable<boolean> {
    return this.userState$.pipe(
      filter((state) => state !== null),
      map(() => this.hasRequiredPermission(required))
    );
  }

  getRoleCountries$(): Observable<string[]> {
    this.log.debug('get role countries');
    return this.getConstraints$().pipe(
      map((constraints) =>
        constraints &&
        constraints.countries &&
        constraints.countries.length &&
        constraints.countries.indexOf('*') < 0
          ? constraints.countries.filter((c) => !!c).map((c) => c.toLowerCase())
          : null
      )
    );
  }

  getAccountManagerIds$(): Observable<string[]> {
    this.log.debug('get account manager ID');
    return this.getConstraints$().pipe(
      map((constraints) => {
        const accountManagerId = constraints?.accountmanagerid;
        if (!accountManagerId) {
          return null;
        }

        return Array.isArray(accountManagerId)
          ? accountManagerId
          : [accountManagerId];
      })
    );
  }

  getLaboratory$(): Observable<string> {
    return this.getConstraints$().pipe(
      map((constraints) =>
        constraints?.laboratory?.length ? constraints?.laboratory[0] : null
      )
    );
  }

  // Waits for the /me response so constraint-based logic never evaluates
  // against an unloaded state.
  private getConstraints$(): Observable<UserConstraints | null> {
    return this.userState$.pipe(
      filter((state) => state !== null),
      map((state: UserData) => state.constraints ?? null)
    );
  }
}
